import { useMemo } from 'react';
import { ErrorBoundary } from './ErrorBoundary';
import { useIsInterviewSession } from '@/entities/agent';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import type { SessionsLayoutProps, SessionsOutletContext } from './SessionsLayout.types';
import { SessionContent } from './SessionContent';

/**
 * `SessionsLayout` — route-element layout that wraps Root + StreamChatSession.
 *
 * Dependency injection: the consuming route wrapper
 * (`app/routes/SessionsLayoutRoute.tsx`) wires all chats + UI/widget store
 * deps and threads them in via `props.deps`. This keeps `features/sessions`
 * cross-feature-OUT and cross-app-OUT clean (FSD canonical).
 *
 * Both the interview and the redesigned shell render through a SINGLE
 * `SessionContent` type. Keeping one component type across an in-session agent
 * switch preserves the `<Outlet>` subtree (Root / StreamChatSession) instead of
 * remounting it — a remount used to reset the chat pages' "run once" refs and
 * revert the user's agent switch.
 */
export const SessionsLayout = ({ deps, children }: SessionsLayoutProps) => {
  const { app, chats, appShell, interview } = deps;

  const currentAgentId = chats.useCurrentAgentIdHook();
  const isInterviewSession = useIsInterviewSession(currentAgentId);
  const selectedWorkspaceId = useCurrentWorkspaceId();

  const outletContext = useMemo<SessionsOutletContext>(
    () => ({
      isInterviewSession,
      InterviewWelcomeMessage: interview.WelcomeMessage,
    }),
    [isInterviewSession, interview.WelcomeMessage],
  );

  return (
    <ErrorBoundary>
      <SessionContent
        isInterviewSession={isInterviewSession}
        app={app}
        chats={chats}
        interview={interview}
        selectedWorkspaceId={selectedWorkspaceId}
        appShell={appShell}
        outletContext={outletContext}
      >
        {children}
      </SessionContent>
    </ErrorBoundary>
  );
};
